/**
 * Which order the shelf is in.
 *
 * The catalogue had a search box and a weight filter and no way to order anything,
 * which is the one control a person actually shopping for yarn reaches for first. Three
 * of the four orders below answer a question a knitter asks out loud in a yarn shop:
 * what is the best value, what is fine enough for this pattern, and which of these
 * gives me the most yardage for one skein.
 *
 * ### Why value rather than price
 *
 * Sorting on the price of a skein would be close to meaningless here. A fifty gram
 * skein and a two hundred and fifty gram cone are both "one skein", so the cheapest
 * skein in the catalogue can easily be the most expensive yarn in it. What compares is
 * price against length, which is what `centsPer100Yards` already derives and what the
 * card already prints next to the price for exactly the same reason.
 *
 * ### Ties keep shelf order
 *
 * Every comparator returns 0 for equal values rather than reaching for a second key,
 * and `Array.prototype.sort` has been a stable sort in every engine since ES2019. So
 * two yarns at the same gauge come out in catalogue order, which is the order they were
 * in before anybody pressed anything. That is worth having: an order that reshuffled
 * equal rows on every keystroke would look like the page had lost its place.
 *
 * Nothing here mutates. `findYarns` hands back a fresh array already, but a sort that
 * reordered its input would be a sort that reordered the catalogue itself the first
 * time somebody passed it `YARNS` directly.
 */

import type { YarnRecord } from './catalogue';
import { centsPer100Yards, shelfEntry } from './shop';

export type SortKey = 'shelf' | 'value' | 'gauge' | 'skein';

export interface SortOrder {
  readonly key: SortKey;
  /** What the option says in the control. */
  readonly label: string;
}

/**
 * The four orders, in the order they are offered.
 *
 * Shelf first because it is the default and because "how the shop chose to lay this
 * out" is a real answer rather than an absence of one.
 */
export const SORT_ORDERS: ReadonlyArray<SortOrder> = Object.freeze([
  { key: 'shelf', label: 'Shelf order' },
  { key: 'value', label: 'Best value per 100 yd' },
  { key: 'gauge', label: 'Finest gauge first' },
  { key: 'skein', label: 'Longest skein first' },
]);

/** Whether a string is one of the four keys, for reading a control's value back. */
export const isSortKey = (value: string): value is SortKey =>
  SORT_ORDERS.some((order) => order.key === value);

/** Price against length, in whole cents, which is the only comparable price figure. */
const valueOf = (yarn: YarnRecord): number => centsPer100Yards(yarn, shelfEntry(yarn.id));

/**
 * The yarns in the chosen order, as a new array.
 *
 * `shelf` returns a copy rather than the input, so every branch of this function has
 * the same contract and no caller has to know which one it took.
 */
export function sortYarns(
  yarns: ReadonlyArray<YarnRecord>,
  key: SortKey,
): ReadonlyArray<YarnRecord> {
  const ordered = [...yarns];

  switch (key) {
    case 'shelf':
      return ordered;
    case 'value':
      // Ascending: cheapest per hundred yards is the best value and comes first.
      return ordered.sort((a, b) => valueOf(a) - valueOf(b));
    case 'gauge':
      // Descending: more stitches to four inches is a finer yarn.
      return ordered.sort((a, b) => b.stsPer4in - a.stsPer4in);
    case 'skein':
      // Descending: the longest skein first, since that is what the question means.
      return ordered.sort((a, b) => b.yardsPerSkein - a.yardsPerSkein);
  }
}
